import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import {
  BookedService,
  BookService,
  CarService,
  ExpensesService,
  FlightService,
  NgxToastrService,
  TrainService,
} from "@core/services";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { TranslateService } from "@ngx-translate/core";
import { AddToCalendarComponent } from "@shared/component/add-to-calendar/add-to-calendar.component";
import { BookedsViewComponent } from "@shared/component/bookeds/bookeds-view/bookeds-view.component";
import { CalendarEvent } from "@shared/interfaces/calendar.interface";
import { CreateCarComponent } from "app/modules/components/cars/custom/create-car/create-car.component";
import { ExpensesComponent } from "app/modules/components/expenses/expenses.component";
import { CreateFlightTrainComponent } from "app/modules/components/flight-train/create-flight-train/create-flight-train.component";
import { CreateCustomHotelComponent } from "app/modules/components/hotel/create-custom-hotel/create-custom-hotel.component";
import moment from "moment";
import { forkJoin, of, Subscription } from "rxjs";
import { catchError, take } from "rxjs/operators";
import { UtilsService } from '../../../../core/services/utils.service';

@Component({
  selector: "app-book-card",
  templateUrl: "./book-card.component.html",
  styleUrls: ["./book-card.component.scss"],
})
export class BookCardComponent implements OnInit {
  @Input() booked: any;
  @Input() companyUuid: string;
  @Input() uuid: string;
  @Input() isUser = true;
  @Input() type: string;
  @Input() userUuid: string;
  @Input() isEmployee: boolean;
  @Input() nextTravelsChecked;
  @Input() index: number;
  @Output() loadType: EventEmitter<any> = new EventEmitter();
  loading = false;
  creator: any;
  subscriptions: Subscription[] = [];
  icon: string;

  defaultImage: string;
  @Input() departmentUuid: string;
  logoReady = false;
  logoError = false;

  constructor(
    private modalService: NgbModal,
    private ngxToastrService: NgxToastrService,
    private translate: TranslateService,
    private bookService: BookService,
    private expensesService: ExpensesService,
    private flightService: FlightService,
    private trainService: TrainService,
    private carService: CarService,
    private bookedService: BookedService,
    private utilsService: UtilsService
  ) {}

  ngOnInit(): void {
    if (this.type?.toLowerCase() === "all" || this.type?.toLowerCase() === "hotel") {
      this.type = this.bookService.getType(this.booked);
    }
    this.getImage();
    this.getServiceIcon();
    if(this.booked?.hotel){
      this.getAddress();
    }
  }

  getImage() {
    switch (this.type?.toLowerCase()) {
      case "car":
        this.defaultImage = "assets/img/gallery/default-car.png";
        break;
      case "flight":
        this.defaultImage = "assets/img/gallery/default-flight.png";
        break;
      case "train":
        this.defaultImage = "assets/img/gallery/default-train.png";
        break;
      case "expense":
        this.defaultImage = "assets/img/svg/default-expense.svg";
        break;
      default:
        this.defaultImage = "assets/img/gallery/image_not_available.png";
        break;
    }
  }

  onChangeTravel() {
    this.isEmployee
      ? this.bookService.onChangeTravel(
          this.booked,
          this.isUser,
          this.companyUuid,
          this.userUuid,
          this.nextTravelsChecked
        )
      : this.isUser
      ? this.bookService.onChangeTravel(
          this.booked,
          this.isUser,
          this.companyUuid,
          this.userUuid,
          this.nextTravelsChecked
        )
      : this.bookService.onChangeTravel(
          this.booked,
          this.isUser,
          this.companyUuid,
          this.userUuid,
          this.nextTravelsChecked
        );
  }

  hasPdf() {
    return this.bookService.hasPdf(this.booked, this.type);
  }

  onGetPDF() {
    this.bookService.onGetPDF(this.booked, this.type);
  }

  advicePdf() {
    this.bookService.advicePdf();
  }

  canCancel() {
    let res = false;
    switch (this.type?.toLowerCase()) {
      case "bookingapireference":
        const today = moment();
        res = this.booked.hotel &&
          this.booked.status?.toLowerCase() !== "cancelled" &&
          today.isSameOrBefore(this.booked.maxCancellationDate) &&
          +this.booked.price >= (this.booked.penality || 0) * 100;
        break;
      case "custom-booking":
        res = false
        break;
      case "car":
        res = false
        break;
      case "flight":
        res = this.isEmployee &&
          this.booked.status?.toLowerCase() !== "cancelled" &&
          moment(this.booked.departDate).isAfter(moment());
        break;
      case "train":
        res =
          /* !this.booked.providerTicketId && */
          this.isEmployee &&
          this.booked.status?.toLowerCase() !== "cancelled" &&
          moment(this.booked.departDate).isAfter(moment());
        break;
      case "expense":
        res = false;
        break;
      default:
        break;
    }
    return res;
  }

  onCancel() {
    this.bookService.onCancel(
      this.booked,
      this.type,
      this.isUser ? null : this.userUuid,
      this.companyUuid
    );
  }

  getServiceIcon() {
    switch (this.type?.toLowerCase()) {
      case "flight":
        this.icon = "assets/img/svg/flight-black.svg";
        break;
      case "train":
        this.icon = "assets/img/svg/sidebar/train.svg";
        break;
      case "expense":
        this.icon = "assets/img/svg/black-car.svg";
        break;
      case "car":
        this.icon = "assets/img/svg/black-car.svg";
        break;
      default:
        this.icon = "assets/img/svg/black-car.svg";
        break;
    }
  }

  getAddress(): string {
    let { address, destinationName } = this.booked?.hotel;
    if (!address) return '';
  
    const originalAddress = address;
    const normalizedDestinationName = destinationName
      ? destinationName.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
      : '';
  
    if (normalizedDestinationName) {
      const regex = new RegExp(`\\b${normalizedDestinationName}\\b`, 'i');
      const parts = originalAddress.split(',').map(part => part.trim());
      const filteredParts = parts.filter(part => {
        const normalizedPart = part.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
        return !regex.test(normalizedPart);
      });
      address = filteredParts.join(', ').replace(/\s*,\s*,/g, ',').replace(/,\s*$/, '').trim();
    }
    this.booked.hotel.address = address;
    return this.booked.hotel.address;
  }

  onEditGetPDF(booked) {
    const downloadRequests = [];
    const fileNameRequests = [];
  
    if (this.type?.toLowerCase() === "hotel" || this.type?.toLowerCase() === "bookingapireference") {
      for (let fileUrl of booked.bonoFileUrl) {
        if (typeof fileUrl === 'string') {
          const uuid = fileUrl.split('/media/')[1];
          
          fileNameRequests.push(this.bookedService.getFileName(uuid).pipe(take(1)));
          downloadRequests.push(this.bookedService.getBookedsPdf(fileUrl).pipe(take(1)));
        }
      }
  
      if (downloadRequests.length > 0) {
        this.subscriptions.push(
          forkJoin({
            responses: forkJoin(downloadRequests),
            fileNames: forkJoin(fileNameRequests.map(request =>
              request.pipe(
                catchError(() => of({ fileName: null }))
              )
            ))
          }).subscribe(
            ({ responses, fileNames }) => {
              const blobUrls = responses.map((res) => URL.createObjectURL(res));
        
              const files = responses.map((res, index) => {
                const fileType = res.type.split("/")[1];
                const defaultFileName = `hotel_${index + 1}.${fileType}`;
                const name = fileNames[index]?.fileName || defaultFileName;
        
                return {
                  blob: res,
                  name: name,
                  url: booked.bonoFileUrl[index],
                };
              });
        
              this.onEdit(blobUrls, files);
            },
            (error) => {
              this.onEdit();
            }
          )
        );
      }else{
        this.onEdit();
      }
    } else if (this.type?.toLowerCase() === "custom-booking") {
      const downloadRequests = [];
      for (let file of booked.voucherFile) {
        downloadRequests.push(
          this.bookedService
            .getCustomBookingPdf(`/media/${file.uuid}/custom`)
            .pipe(take(1))
        );
      }
    
      if (downloadRequests && downloadRequests.length > 0) {
        this.subscriptions.push(
          forkJoin(downloadRequests).subscribe(
            (responses) => {
              const blobUrls = responses.map((res) => URL.createObjectURL(res));
              const files = responses.map((res, index) => {
                
                return {
                  blob: res,
                  name: `${booked.voucherFile[index].fileName}`,
                  url: `/media/${booked.voucherFile[index].uuid}`,
                };
              });
    
              this.onEdit(blobUrls, files);
            },
            (error) => {
              this.onEdit();
            }
          )
        );
      } else {
        this.onEdit();
      }
    } else {
      const fileUrls = booked.voucherFileUrl || booked.bonoFileUrl;
      if (Array.isArray(fileUrls) && fileUrls.length > 0) {
        for (let fileUrl of fileUrls) {
          downloadRequests.push(this.bookedService.getBookedsPdf(fileUrl).pipe(take(1)));
        }

        if (downloadRequests.length > 0) {
          this.subscriptions.push(
            forkJoin(downloadRequests).subscribe(
              (responses) => {
                const blobUrls = responses.map((res) => URL.createObjectURL(res));
                this.translate.get(`common.${this.type}`).subscribe((translatedType) => {
                  const files = responses.map((res, index) => {
                  const fileType = res.type.split("/")[1];
                  const fileName = booked.voucherFile && booked.voucherFile[index] && booked.voucherFile[index].fileName
                    ? booked.voucherFile[index].fileName
                    : `${translatedType}_${index + 1}.${fileType}`;
                    return {
                      blob: res,
                      name: fileName,
                      url: fileUrls[index],
                    };
                  });
                  this.onEdit(blobUrls, files);
                });
              },
              (error) => {
                this.onEdit();
              }
            )
          );
        }
      }else if (fileUrls && !Array.isArray(fileUrls)) {
        this.subscriptions.push(
          this.bookedService
            .getBookedsPdf(fileUrls)
            .pipe(take(1))
            .subscribe(
              (res) => {
                const blobUrl = URL.createObjectURL(res);
                const file = res;
                const fileType = file.type.split("/")[1];
                const fileName = booked.voucherFile && booked.voucherFile[0] && booked.voucherFile[0].fileName
                    ? booked.voucherFile[0].fileName
                    : `${this.type}.${fileType}`;
                file["name"] = fileName;
                this.onEdit(blobUrl, file);
              },
              (error) => {
                this.onEdit();
              }
            )
        );
      }
    }
    if(booked.voucherFileUuid) {
      let mediaUrl = this.type?.toLowerCase() === "flight" ? "/media/flight/" : (this.type?.toLowerCase() === "expense" ? "/media/expense/" : (this.type?.toLowerCase() === "train" ? "/media/train/" : (this.type?.toLowerCase() === "car" ? "/media/car/" : '/media/')))
      this.bookedService.getBookedsPdf(this.type?.toLowerCase() !== "custom-booking" ? mediaUrl + booked.voucherFileUuid : "/media/"+booked.voucherFileUuid+"/custom" ).pipe(take(1)).subscribe(
        (res) => {
          const blobUrl = URL.createObjectURL(res);
          const file = res;
          const fileType = file.type.split("/")[1];
          const fileName = booked.voucherFile && booked.voucherFile[0] && booked.voucherFile[0].fileName
              ? booked.voucherFile[0].fileName
              : `${this.type}.${fileType}`;
          file["name"] = fileName;
          this.onEdit(blobUrl, file);
        },
        (error) => {
          this.onEdit();
        }
      )
    }
  }

  reduceComments(message: string) {
    return message.length > 40 ? `${message.substring(0, 39)}...` : message;
  }

  onView() {
    const transactions = this.bookService.getData('transactions');
    let transactionFound
    if(this.isEmployee && transactions){
      transactionFound = transactions.find(transaction => {
        if (Array.isArray(transaction.bookedObject)) {
          return transaction.bookedObject.some(b => b.uuid === this.booked.uuid);
        }
        return transaction.bookedObject?.uuid === this.booked.uuid;
      });

      if(transactionFound){
        this.booked.order = transactionFound.order;
      }
    }
    const editCar = this.modalService.open(BookedsViewComponent, {
      size: "lg",
      centered: true,
    });
    if(this.type?.toLowerCase() == "car"){
      editCar.componentInstance.type = this.booked.carType;
    }  
    editCar.componentInstance.booking = this.booked;
    editCar.componentInstance.type = this.type;
    editCar.componentInstance.isUser = this.isUser;
    editCar.componentInstance.isEmployee = this.isEmployee;
    if (!this.isUser && !this.booked.seatTicketIds) {
      editCar.componentInstance.pnr = this.booked.pnr;
    }
  }

  onEdit(blobUrl?, file?) {
    switch (this.type?.toLowerCase()) {
      case "car":
        const editCar = this.modalService.open(CreateCarComponent, {
          size: "lg",
          centered: true,
        });
        editCar.componentInstance.isAdmin = !this.isUser;
        editCar.componentInstance.loading = this.loading;
        editCar.componentInstance.companyUuid = this.companyUuid;
        editCar.componentInstance.userId = this.userUuid ? this.userUuid : this.booked.userUuid;
        editCar.componentInstance.data = this.booked;
        editCar.componentInstance.isEdit = true;
        if (blobUrl && file) {
          editCar.componentInstance.blobUrl = blobUrl;
          editCar.componentInstance.fileBack = file;
        }
        editCar.componentInstance.sendInfo.subscribe((info) => {
          editCar.componentInstance.loading = true;
          const formData = new FormData();
          if(info.file){
            if (info.file && Array.isArray(info.file)) {
              info.file.forEach((file: any, index: number) => {
                if (file.blob && file.name) {
                  const newFile = new File([file.blob], file.name, { type: file.blob.type });
                  formData.append("file", newFile, newFile.name);
                } 
                else if (file instanceof File) {
                  formData.append("file", file, file.name);
                } 
                else if (file instanceof Blob) {
                  const newFile = new File([file], `file_${index}.pdf`, { type: file.type });
                  formData.append("file", newFile, newFile.name);
                } 
              });
            } 
          }

          if (info.filesToRemove && info.filesToRemove.length > 0) {
            info.filesToRemove.forEach((fileUrl, index) => {
              formData.append(`filesToRemove[]`, fileUrl);
            });
          }
          
          formData.append("userUuid", info.userUuid);
          formData.append("from", info.from);
          formData.append("to", info.to);
          formData.append("pickupPlaceFrom", info.pickupPlaceFrom);
          formData.append("pickupPlaceTo", info.pickupPlaceTo);
          formData.append("price", info.price || info.amount);
          if(info.wholesalePrice) {
            formData.append("wholesalePrice", info.wholesalePrice);
          }
          formData.append("age", info.age);
          if (info.travelName) {
            formData.append("travelName", info.travelName);
          }
          formData.append("customCode", info.customCode);
          formData.append("costCenter", info.costCenter);
          if(info.supplierInvoice) {
            formData.append("supplierInvoice", info.supplierInvoice);
          }
          if(info.bookType) {
            formData.append("bookType", info.bookType);
          }
          if(info.invoiced) {
            formData.append("invoiced", info.invoiced);
          }
          formData.append("freeTaxes", info.freeTaxes);
          formData.append("departDate", info.departDate);
          formData.append("returnDate", info.returnDate);
          if (info.providerUuid) {
            formData.append("providerUuid", info.providerUuid);
          }
          formData.append("carType", info.carType);
          formData.append("insuranceWithExcess", info.insuranceWithExcess);
          formData.append("comments", info.comments);
          if(info.passenger){
            formData.append("passenger", info.passenger);
          }
          this.carService.editCar(formData, this.booked.uuid).subscribe(
            (result) => {
              editCar.componentInstance.loading = false;
              editCar.dismiss("Send Info");
              this.translate
                .get(["common.congrat", `car.edit-car.edited-car`])
                .subscribe((resp) => {
                  this.ngxToastrService.typeSuccess(
                    resp["common.congrat"],
                    resp[`car.edit-car.edited-car`]
                  );
                  this.loadType.emit(this.type);
                });
            },
            (err) => {
              this.handleEditValidationError(
                err,
                editCar,
                'car',
                'car.edit-car.error-edit-car'
              );
            }
          );
        });

        break;
      case "flight":
        const editFlight = this.modalService.open(CreateFlightTrainComponent, {
          size: "lg",
          centered: true,
        });
        editFlight.componentInstance.type = this.type;
        editFlight.componentInstance.data = this.booked;
        editFlight.componentInstance.userId = this.booked?.userUuid ? this.booked.userUuid : this.userUuid;
        editFlight.componentInstance.isAdmin = !this.isUser;
        editFlight.componentInstance.loading = this.loading;
        editFlight.componentInstance.companyUuid = this.companyUuid;
        if (blobUrl && file) {
          editFlight.componentInstance.blobUrl = blobUrl;
          editFlight.componentInstance.fileBack = file;
        }
        editFlight.componentInstance.sendInfo.subscribe((info) => {
          editFlight.componentInstance.loading = true;
          const formData = new FormData();
          if(info.file){
            if (info.file && Array.isArray(info.file)) {
              info.file.forEach((file: any, index: number) => {
                if (file.blob && file.name) {
                  const newFile = new File([file.blob], file.name, { type: file.blob.type });
                  formData.append("file", newFile, newFile.name);
                } 
                else if (file instanceof File) {
                  formData.append("file", file, file.name);
                } 
                else if (file instanceof Blob) {
                  const newFile = new File([file], `file_${index}.pdf`, { type: file.type });
                  formData.append("file", newFile, newFile.name);
                } 
              });
            } 
          }

          if (info.filesToRemove && info.filesToRemove.length > 0) {
            info.filesToRemove.forEach((fileUrl, index) => {
              formData.append(`filesToRemove[]`, fileUrl);
            });
          }

          formData.append("companyUuid", info.companyUuid);
          formData.append("userUuid", info.userId);
          formData.append("from", info.origin);
          formData.append("to", info.destination);
          formData.append("departDate", info.departure);
          formData.append("departLocalDate", this.formatLocalDate(new Date(info.departure)));
          formData.append("observations", info.annotations);
          formData.append("price", info.price);
          formData.append("oneWay", info.oneWay);
          formData.append("freeTaxes", info.freeTaxes);
          if (info.wholesalePrice) {
            formData.append("wholesalePrice", info.wholesalePrice);
          }
          if (!info.oneWay) {
            formData.append("returnDate", info.return);
            formData.append("returnLocalDate", this.formatLocalDate(new Date(info.return)));
          }
          if(info.pnr) {
            formData.append("pnr", info.pnr);
          }
          if(info.travellers) {
            formData.append("travellers", JSON.stringify(info.travellers));
          }
          formData.append("customCode", info.customCode);
          formData.append("costCenter", info.costCenter);
          if(info.supplierInvoice) {
            formData.append("supplierInvoice", info.supplierInvoice);
          }
          if(info.bookType) {
            formData.append("bookType", info.bookType);
          }
          if(info.invoiced) {
            formData.append("invoiced", info.invoiced);
          }
          if(info.passenger){
            formData.append("passenger", info.passenger)
          }
          if(info.originalAmount != null){
            formData.append("originalAmount", info.originalAmount)
          }
          if(info.totalPrice){
            formData.append("totalPrice", info.totalPrice)
          }
          if (info.relatedFlights) {
            formData.append("relatedFlights", JSON.stringify(info.relatedFlights));
          }
          if(info.departureAirline) {
            formData.append("departureAirline", info.departureAirline)
          }
          if(info.returnAirline) {
            formData.append("returnAirline", info.returnAirline)
          }
          if(info.class) {
            formData.append("class", info.class)
          }
          if(info.seats) {
            formData.append("seats", JSON.stringify(info.seats));
          }
          if (info.providerUuid) {
            formData.append("providerUuid", info.providerUuid);
          }
          this.flightService.editFlight(this.booked.uuid, formData).subscribe(
            (result) => {
              editFlight.componentInstance.loading = false;
              editFlight.dismiss("Send Info");
              this.translate
                .get([
                  "common.congrat",
                  `${this.type?.toLowerCase()}.edit-${this.type?.toLowerCase()}.edited-${this.type?.toLowerCase()}`,
                ])
                .subscribe((resp) => {
                  this.ngxToastrService.typeSuccess(
                    resp["common.congrat"],
                    resp[
                      `${this.type?.toLowerCase()}.edit-${this.type?.toLowerCase()}.edited-${this.type?.toLowerCase()}`
                    ]
                  );
                  this.loadType.emit(this.type);
                });
            },
            (err) => {
              this.handleEditValidationError(
                err,
                editFlight,
                'flight',
                'flight.edit-flight.error-edit-flight'
              );
            }
          );
        });
        break;
      case "train":
        const editTrain = this.modalService.open(CreateFlightTrainComponent, {
          size: "lg",
          centered: true,
        });
        editTrain.componentInstance.type = this.type;
        editTrain.componentInstance.data = this.booked;
        editTrain.componentInstance.userId = this.userUuid ? this.userUuid : this.booked.userUuid;
        editTrain.componentInstance.isAdmin = !this.isUser;
        editTrain.componentInstance.loading = this.loading;
        editTrain.componentInstance.companyUuid = this.companyUuid;
        if (blobUrl && file) {
          editTrain.componentInstance.blobUrl = blobUrl;
          editTrain.componentInstance.fileBack = file;
        }
        /*this.trainService.editTrain(booked.uuid, formData)*/
        editTrain.componentInstance.sendInfo.subscribe((info) => {
          editTrain.componentInstance.loading = true;
          const formData = new FormData();
          if(info.file){
            if (info.file && Array.isArray(info.file)) {
              info.file.forEach((file: any, index: number) => {
                if (file.blob && file.name) {
                  const newFile = new File([file.blob], file.name, { type: file.blob.type });
                  formData.append("file", newFile, newFile.name);
                } 
                else if (file instanceof File) {
                  formData.append("file", file, file.name);
                } 
                else if (file instanceof Blob) {
                  const newFile = new File([file], `file_${index}.pdf`, { type: file.type });
                  formData.append("file", newFile, newFile.name);
                } 
              });
            } 
          }
          if (info.filesToRemove && info.filesToRemove.length > 0) {
            info.filesToRemove.forEach((fileUrl, index) => {
              formData.append(`filesToRemove[]`, fileUrl);
            });
          }
          formData.append("companyUuid", info.companyUuid);
          formData.append("userUuid", info.userId);
          if (info.origin)
            formData.append("origin", typeof info.origin === 'string' ? JSON.stringify({name: info.origin}) : JSON.stringify(info.origin));        
          if (info.destination)
            formData.append("destination", typeof info.destination === 'string' ? JSON.stringify({name: info.destination}) : JSON.stringify(info.destination));
          formData.append("departDate", info.departure);
          formData.append("observations", info.annotations);
          formData.append("price", info.price);
          if (info.wholesalePrice) {
            formData.append("wholesalePrice", info.wholesalePrice);
          }
          formData.append("oneWay", info.oneWay);
          formData.append("freeTaxes", info.freeTaxes);
          if (!info.oneWay) {
            formData.append("returnDate", info.return);
          }
          formData.append("customCode", info.customCode);
          formData.append("costCenter", info.costCenter);
          if(info.supplierInvoice) {
            formData.append("supplierInvoice", info.supplierInvoice);
          }
          if(info.bookType) {
            formData.append("bookType", info.bookType);
          }
          if(info.invoiced) {
            formData.append("invoiced", info.invoiced);
          }
          if(info.passenger){
            formData.append("passenger", info.passenger)
          }
          if(info.departureCarrier) {
            formData.append("departureCarrier", info.departureCarrier)
          }
          if(info.returnCarrier) {
            formData.append("returnCarrier", info.returnCarrier)
          }
          if(info.class) {
            formData.append("class", info.class)
          }
          if(info.pnr) {
            formData.append("pnr", info.pnr);
          }
          if (info.providerUuid) {
            formData.append("providerUuid", info.providerUuid);
          }
          if(info.seats) {
            formData.append("seats", JSON.stringify(info.seats));
          }
          this.trainService.editTrain(this.booked.uuid, formData).subscribe(
            (result) => {
              editTrain.componentInstance.loading = false;
              editTrain.dismiss("Send Info");
              this.translate
                .get([
                  "common.congrat",
                  `${this.type?.toLowerCase()}.edit-${this.type?.toLowerCase()}.edited-${this.type?.toLowerCase()}`,
                ])
                .subscribe((resp) => {
                  this.ngxToastrService.typeSuccess(
                    resp["common.congrat"],
                    resp[
                      `${this.type?.toLowerCase()}.edit-${this.type?.toLowerCase()}.edited-${this.type?.toLowerCase()}`
                    ]
                  );
                  this.loadType.emit(this.type);
                });
            },
            (err) => {
              this.handleEditValidationError(
                err,
                editTrain,
                'train',
                'train.edit-train.error-edit-train'
              );
            }
          );
        });
        break;
      case "expense":
        const editExpense = this.modalService.open(ExpensesComponent, {
          size: "lg",
          centered: true,
        });

        editExpense.componentInstance.userUuid = this.userUuid ? this.userUuid : this.booked.userUuid;
        editExpense.componentInstance.companyUuid = this.companyUuid;
        editExpense.componentInstance.departmentUuid =
          this.departmentUuid;
        editExpense.componentInstance.expense = this.booked;
        if (blobUrl && file) {
          editExpense.componentInstance.blobUrl = blobUrl;
          editExpense.componentInstance.fileBack = file;
        }
        editExpense.componentInstance.sendInfo.subscribe((body) => {
          const formData = new FormData();
          if(body.file){
            if (body.file && Array.isArray(body.file)) {
              body.file.forEach((file: any, index: number) => {
                if (file.blob && file.name) {
                  const newFile = new File([file.blob], file.name, { type: file.blob.type });
                  formData.append("file", newFile, newFile.name);
                } 
                else if (file instanceof File) {
                  formData.append("file", file, file.name);
                } 
                else if (file instanceof Blob) {
                  const newFile = new File([file], `file_${index}.pdf`, { type: file.type });
                  formData.append("file", newFile, newFile.name);
                } 
              });
            } 
          }
          if (body.filesToRemove && body.filesToRemove.length > 0) {
            body.filesToRemove.forEach((fileUrl, index) => {
              formData.append(`filesToRemove[]`, fileUrl);
            });
          }
          formData.append("departmentUuid", body.departmentUuid);
          formData.append("companyUuid", body.companyUuid);
          formData.append("userUuid", body.userUuid);
          formData.append("price", body.price || body.amount);
          formData.append("beginDate", body.beginDate);
          formData.append("endDate", body.endDate);
          formData.append("type", body.type);
          formData.append("freeTaxes", body.freeTaxes);
          formData.append("customCode", body.customCode);
          formData.append("costCenter", body.costCenter);
          if(body.wholesalePrice){
            formData.append("wholesalePrice", body.wholesalePrice);
          }
          if(body.supplierInvoice) {
            formData.append("supplierInvoice", body.supplierInvoice);
          }
          if(body.bookType) {
            formData.append("bookType", body.bookType);
          }
          if(body.invoiced) {
            formData.append("invoiced", body.invoiced);
          }
          if (body.comments) {
            formData.append("comments", body.comments);
          }
          if (body.adminComments) {
            formData.append("adminComments", body.adminComments);
          }
          if (body.annotationsExtras) {
            formData.append("annotationsExtras", body.annotationsExtras);
          }
          if(body.passenger){
            formData.append("passenger", body.passenger);
          }
          if (body.providerUuid) {
            formData.append("providerUuid", body.providerUuid);
          }
          this.subscriptions.push(
            this.expensesService
              .editExpense(this.booked.uuid, formData)
              .subscribe(
                (s) => {
                  this.translate.get("expenses.success").subscribe((result) => {
                    this.ngxToastrService.typeSuccess(null, result);
                  });
                  editExpense.dismiss();
                  this.loadType.emit(this.type);
                },
                (err) => {
                  this.handleEditValidationError(
                    err,
                    editExpense,
                    'expense',
                    'expenses.error'
                  );
                }
              )
          );
        });
        break;
      case "hotel":
      case "custom-booking":
      case "bookingapireference":
        const editHotel = this.modalService.open(CreateCustomHotelComponent, {
          size: "lg",
          centered: true,
        });
        editHotel.componentInstance.type = "hotel";
        editHotel.componentInstance.data = this.booked;
        editHotel.componentInstance.userId = this.userUuid 
          ? this.userUuid 
          : Array.isArray(this.booked.userUuid) && this.booked.userUuid.length > 0
            ? this.booked.userUuid[0]
            : this.booked.userUuid;
        editHotel.componentInstance.isAdmin = !this.isUser;
        editHotel.componentInstance.companyUuid = this.companyUuid;
        editHotel.componentInstance.loading = this.loading;
        editHotel.componentInstance.editing = true;
        if (blobUrl && file) {
          editHotel.componentInstance.blobUrl = blobUrl;
          editHotel.componentInstance.fileBack = file;
        }
        editHotel.componentInstance.sendInfo.subscribe((body) => {
          const formData = new FormData();
          formData.append("companyUuid", this.companyUuid);

          if (this.booked.hotel) {
            const internalTravelers = Array.isArray(body.traveler)
              ? body.traveler.filter((traveler) => !!traveler?.uuid)
              : [];

            const userUuids = internalTravelers.length > 0
              ? internalTravelers.map((traveler) => traveler.uuid)
              : Array.isArray(this.booked.userUuid)
                ? this.booked.userUuid
                : [this.booked.userUuid];

            userUuids
              .filter(Boolean)
              .forEach((uuid) => {
                formData.append("userUuid[]", uuid);
              });
          } else {
            const userUuidValue = Array.isArray(this.userUuid) ? this.userUuid[0] : this.userUuid;
            const fallbackUserUuid = Array.isArray(this.booked.userUuid) ? this.booked.userUuid[0] : this.booked.userUuid;

            formData.append("userUuid", userUuidValue ? userUuidValue : fallbackUserUuid);
          }
          
          formData.append("comments", body.comments);
          formData.append("price", body.price);
          if (body.wholesalePrice) {
            formData.append("wholesalePrice", body.wholesalePrice);
          }
          if (!this.booked.hotel) {
            formData.append("city", body.city);
            formData.append("hotel", body.hotel);
          }else {
            formData.append("destinationName", body.city);
          }

           if (body.file) {
            if (body.file && Array.isArray(body.file)) {
              body.file.forEach((file: any, index: number) => {
                if (file.blob && file.name) {
                  const newFile = new File([file.blob], file.name, { type: file.blob.type });
                  formData.append("file", newFile, newFile.name);
                } 
                else if (file instanceof File) {
                  formData.append("file", file, file.name);
                } 
                else if (file instanceof Blob) {
                  const newFile = new File([file], `file_${index}.pdf`, { type: file.type });
                  formData.append("file", newFile, newFile.name);
                } 
              });
            } 
           }
          if (body.filesToRemove && body.filesToRemove.length > 0) {
            body.filesToRemove.forEach((fileUrl, index) => {
              formData.append(`filesToRemove[]`, fileUrl);
            });
          }
          if(body.marketingFee) {
            formData.append("marketingFee", body.marketingFee);
          }
          if(body.providerUuid) {
            formData.append("providerUuid", body.providerUuid);
          }
          formData.append("checkIn", body.checkIn);
          formData.append("checkOut", body.checkOut);
          formData.append("customCode", body.customCode);
          formData.append("costCenter", body.costCenter);
          if(body.supplierInvoice) {
            formData.append("supplierInvoice", body.supplierInvoice);
          }
          if(body.bookType) {
            formData.append("bookType", body.bookType);
          }
          if(body.invoiced) {
            formData.append("invoiced", body.invoiced);
          }
          if(body.freeTaxes){
            formData.append("freeTaxes", body.freeTaxes);
          }

          if (body.traveler) {
            if (this.booked.hotel) {
              const travelers = Array.isArray(body.traveler)
                ? body.traveler
                : [body.traveler];

              formData.append("traveler", JSON.stringify(travelers));
            } else {
              const travelerValue = Array.isArray(body.traveler)
                ? body.traveler[0]
                : body.traveler;

              const travelerUuid =
                typeof travelerValue === 'string'
                  ? travelerValue
                  : travelerValue?.uuid;

              if (travelerUuid) {
                formData.append("traveler", travelerUuid);
              }
            }
          }
          if(this.booked.hotel && body.address){
            formData.append("address", body.address)
          }
          if(body.locator){
            formData.append("locator", body.locator);
          }
          if (body.rooms) {
            formData.append("rooms", body.rooms);
          }
          if (body.roomType) {
            formData.append("roomType", body.roomType);
          }
          if (body.boardType) {
            formData.append("boardType", body.boardType);
          }
          if (body.guests && Array.isArray(body.guests)) {
            body.guests.forEach((guest, index) => {
              formData.append(`guests[${index}][name]`, guest.name || '');
              formData.append(`guests[${index}][surname]`, guest.surname || '');
            });
          }
          if (body.travelersPerRoom) {
            const formattedTravelersPerRoom = body.travelersPerRoom.map((travelers, index) => ({
              roomNumber: index + 1,
              travelers: travelers
            }));
            
            formData.append("travelersPerRoom", JSON.stringify(formattedTravelersPerRoom));
          }
          const action = this.booked.hotel
            ? this.bookedService.putBooking(this.booked.uuid, formData)
            : this.bookedService.putCustomBooking(this.booked.uuid, formData);
          action.subscribe(
            (res) => {
              editHotel.componentInstance.loading = false;
              editHotel.dismiss("Send Info");
              this.translate
                .get(["common.congrat", `hotel.edited-success`])
                .subscribe((resp) => {
                  this.ngxToastrService.typeSuccess(
                    resp["common.congrat"],
                    resp[`hotel.edited-success`]
                  );
                  this.loadType.emit(this.type);
                });
            },
            (err) => {
              this.handleEditValidationError(
                err,
                editHotel,
                'hotel',
                'hotel.edited-error'
              );
            }
          );
        });
        break;
      default:
        break;
    }
  }

  formatLocalDate(d) {
    const pad = n => n.toString().padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }

  parseDateTime(date: string) {
    return moment(date);
  }

  getLogo(value: string) {
    return `assets/img/airlines/small/${value}.png`;
  }

  getDate(date) {
    return moment(date).tz('Europe/Madrid').format('DD/MM/YYYY HH:mm');
  }

  deletePermanently(booking: any) {
    this.bookService.deleteForever(booking).subscribe(
      (res) => {
        this.translate.get(`hotel.deleted-success`).subscribe((successMessage) => {
          this.ngxToastrService.typeSuccess(successMessage, "");
        });
        this.loadType.emit(this.type);
      },
      (err) => {
        this.translate.get(`hotel.deleted-error`).subscribe((result) => {
          this.ngxToastrService.typeError(result, err.error.message);
        });
      }
    );
  }

  transformDate(date) {
    return moment(date).format('DD/MM/YYYY HH:mm')
  }

  private tt = (k: string, fb: string) => this.translate?.instant?.(k) || fb;

  private toDate(v: any): Date {
    return v instanceof Date ? v : new Date(v);
  }

  private pickBestDate(local?: string, utc?: string): Date {
    return local ? new Date(local) : (utc ? new Date(utc) : new Date());
  }

  private addDays(d: any, days: number): Date {
    const base = this.toDate(d);
    const x = new Date(base.getTime());
    x.setDate(x.getDate() + days);
    return x;
  }

  private formatRoute(from?: string, to?: string) {
    const a = (from || '').toString().trim();
    const b = (to   || '').toString().trim();
    return (a && b) ? `${a} -> ${b}` : (a || b || '');
  }

  private escapeHtmlAllowBasic(str: string = ''): string {
    return String(str)
      .replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;')
      .replace(/"/g,'&quot;').replace(/'/g,'&#039;');
  }

  private stripHtml(html: string): string {
    const div = document.createElement('div');
    div.innerHTML = html || '';
    return (div.textContent || div.innerText || '').trim();
  }

  private slugify(s: string): string {
    return (s || 'evento')
      .toLowerCase()
      .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  }

  private normalizeHotelBooking(b: any) {
    const h = (b?.hotel || b?.accommodation) ?? {};
    const hasHotelObj = !!(b?.hotel || b?.accommodation);

    const name        = hasHotelObj ? (h.name ?? '') : (b?.hotelName ?? '');
    const address     = hasHotelObj ? (h.address ?? '') : (b?.address ?? b?.destinationName ?? '');
    const zoneName    = hasHotelObj ? (h.zoneName ?? '') : '';
    const web         = hasHotelObj ? (h.web ?? '') : '';
    const email       = hasHotelObj ? (h.email ?? '') : '';
    const phone       = hasHotelObj ? (h.phone ?? '') : '';
    const description = hasHotelObj ? (h.description ?? '') : '';

    const locator     = b?.locator ?? '';
    const rooms       = (typeof b?.rooms === 'number') ? b.rooms : '';
    const roomType    = b?.roomType ?? '';
    const boardType   = b?.boardType ?? '';

    return { name, address, zoneName, web, email, phone, description, locator, rooms, roomType, boardType, hasHotelObj };
  }

  private buildFlightHtml(booked: any, leg: 'outbound'|'return'): string {
    const sectionTitle = (leg === 'outbound')
      ? this.tt('my-bookeds.train-flight.one-way', 'Ida')
      : this.tt('flight.form-flight.return', 'Vuelta');

    const locatorLbl = this.tt('my-bookeds.train-flight.locator', 'Localizador');
    const notesLbl   = this.tt('csv.observations', 'Observaciones');
    const timeLbl    = this.tt('event-calendar.flight-time', 'Tiempo de vuelo');
    const arrLbl     = this.tt('event-calendar.arrival-datetime', 'Fecha de llegada');

    const pnr      = booked?.pnr;
    const notes    = booked?.observations || booked?.comments;
    const duration = booked?.totalTime?.[leg];
    const arrival  = booked?.arrivalDate?.[leg];
    const arrivalDisp = arrival ? this.getDate(arrival) : '';

    const items: string[] = [];
    if (pnr)         items.push(`<li><b>${locatorLbl}: </b>${this.escapeHtmlAllowBasic(pnr)}</li>`);
    if (duration)    items.push(`<li><b>${timeLbl}: </b>${this.escapeHtmlAllowBasic(duration)}</li>`);
    if (arrivalDisp) items.push(`<li><b>${arrLbl}: </b>${this.escapeHtmlAllowBasic(arrivalDisp)}</li>`);

    const listHtml = items.length ? `<h2>${sectionTitle}</h2><ul>${items.join('')}</ul>` : '';
    const notesHtml = notes ? `<br><h2>${notesLbl}:</h2><br>${this.escapeHtmlAllowBasic(notes)}` : '';

    return (listHtml + notesHtml).trim() || 'https://app.vyootrip.com/';
  }

  private buildTrainHtml(booked: any, leg: 'outbound'|'return'): string {
    const sectionTitle = (leg === 'outbound')
      ? this.tt('my-bookeds.train-flight.one-way', 'Ida')
      : this.tt('flight.form-flight.return', 'Vuelta');

    const locatorLbl = this.tt('my-bookeds.train-flight.locator', 'Localizador');
    const carrierLbl = this.tt('train.form-train.carrier', 'Carrier');
    const classLbl   = this.tt('common.class-flight', 'Clase');
    const extrasLbl  = this.tt('flight.form-flight.extras', 'Extras');

    const locator = booked?.renfeLocalizador ?? booked?.locator;
    const idx     = leg === 'outbound' ? 0 : 1;
    const carrier = Array.isArray(booked?.carriers) ? booked.carriers[idx] : undefined;
    const trainClass = booked?.class;
    const extras  = booked?.observations;

    const items: string[] = [];
    if (locator)    items.push(`<li><b>${locatorLbl}: </b>${this.escapeHtmlAllowBasic(locator)}</li>`);
    if (carrier)    items.push(`<li><b>${carrierLbl}: </b>${this.escapeHtmlAllowBasic(carrier)}</li>`);
    if (trainClass) items.push(`<li><b>${classLbl}: </b>${this.escapeHtmlAllowBasic(trainClass)}</li>`);
    if (extras)     items.push(`<li><b>${extrasLbl}: </b>${this.escapeHtmlAllowBasic(extras)}</li>`);

    return (items.length ? `<h2>${sectionTitle}</h2><ul>${items.join('')}</ul>` : '').trim()
        || 'https://app.vyootrip.com/';
  }

  private pickupTypeLabel(type?: string): string {
    switch ((type || '').trim()) {
      case 'airport':      return this.tt('car.custom.request.pickup.airport', 'Aeropuerto');
      case 'trainStation': return this.tt('car.custom.request.pickup.trainStation', 'Estación de tren');
      case 'office':       return this.tt('car.custom.request.pickup.office', 'Oficina');
      default:             return type || '';
    }
  }

  private formatPickupPlaceValue(place: any): string {
    if (!place) return '';
    if (typeof place === 'string') return this.pickupTypeLabel(place) || place;
    const type = this.pickupTypeLabel(place.type);
    const name = place.name || place.city || '';
    const code = place.code ? ` (${place.code})` : '';
    return (type && (name || place.code)) ? `${type} – ${name}${code}` : (type || name || '');
  }

  private buildCarHtml(booked: any, leg: 'outbound'|'return'): string {
    const sectionTitle = (leg === 'outbound')
      ? this.tt('my-bookeds.train-flight.one-way', 'Ida')
      : this.tt('flight.form-flight.return', 'Vuelta');

    const typeLbl     = this.tt('car.vehicle-type', 'Tipo de coche');
    const pickLbl     = this.tt('car.custom.request.pickup.titleFrom', 'Lugar de recogida');
    const dropLbl     = this.tt('car.custom.request.pickup.titleTo', 'Lugar de devolución');
    const providerLbl = this.tt('car.form-car.provider', 'Proveedor');

    const carType  = booked?.carType;
    const rawPickup  = (leg === 'outbound') ? booked?.pickupPlaceFrom : (booked?.pickupPlaceTo ?? booked?.pickupPlaceFrom);
    const rawDropoff = (leg === 'outbound') ? booked?.pickupPlaceTo   : (booked?.pickupPlaceFrom ?? booked?.pickupPlaceTo);
    const pickup  = this.formatPickupPlaceValue(rawPickup);
    const dropoff = this.formatPickupPlaceValue(rawDropoff);
    const provider = booked?.provider;

    const items: string[] = [];
    if (carType)  items.push(`<li><b>${typeLbl}: </b>${this.escapeHtmlAllowBasic(carType)}</li>`);
    if (pickup)   items.push(`<li><b>${pickLbl}: </b>${this.escapeHtmlAllowBasic(pickup)}</li>`);
    if (dropoff)  items.push(`<li><b>${dropLbl}: </b>${this.escapeHtmlAllowBasic(dropoff)}</li>`);
    if (provider) items.push(`<li><b>${providerLbl}: </b>${this.escapeHtmlAllowBasic(provider)}</li>`);

    return (items.length ? `<h2>${sectionTitle}</h2><ul>${items.join('')}</ul>` : '').trim()
        || 'https://app.vyootrip.com/';
  }


  private buildCalendarEvents(booked: any, type?: string): CalendarEvent[] {
    if (!booked) return [];
    const t = (type || '').toLowerCase();

    if (t === 'bookingapireference' || t === 'custom-booking' || t === 'hotel') {
      return this.buildHotelEvents(booked);
    }
    if (t === 'flight' || t === 'train') {
      return this.buildTransportEvents(booked, t);
    }
    if (t === 'car') {
      return this.buildCarEvents(booked);
    }
    if (t === 'expense') {
      return this.buildExpenseEvent(booked);
    }
    return this.buildGenericEvent(booked);
  }

  private buildHotelEvents(booked: any): CalendarEvent[] {
    const H = this.normalizeHotelBooking(booked);

    const titlePrefix = this.tt('event-calendar.title', 'Reserva: ');
    const addr = (H.address || booked?.destinationName || '').trim();
    const loc  = H.zoneName ? `${addr}, ${H.zoneName}` : addr;

    const dataTitle = this.tt('event-calendar.contact', 'Datos de contacto');
    const mailLbl   = this.tt('event-calendar.mail', 'Email');
    const phoneLbl  = this.tt('event-calendar.phone', 'Teléfono');
    const descLbl   = this.tt('event-calendar.accommodation', 'Descripción del alojamiento');

    const contactHtml = (H.email || H.phone || H.description)
      ? `<h2>${dataTitle}</h2><ul>` +
        (H.email ? `<li><b>${mailLbl}: </b>${this.escapeHtmlAllowBasic(H.email)}</li>` : '') +
        (H.phone ? `<li><b>${phoneLbl}: </b>${this.escapeHtmlAllowBasic(H.phone)}</li>` : '') +
        `</ul>` +
        (H.description ? `<br><h2>${descLbl}:</h2><br>${this.escapeHtmlAllowBasic(H.description)}` : '')
      : '';

    const html = contactHtml || (booked?.comments || 'https://app.vyootrip.com/');

    const checkIn  = this.toDate(booked?.checkIn);
    const checkOut = booked?.checkOut ? this.toDate(booked.checkOut) : this.addDays(checkIn, 1);
    const start = new Date(checkIn.getFullYear(), checkIn.getMonth(), checkIn.getDate(), 0, 0, 0, 0);
    const end   = new Date(checkOut.getFullYear(), checkOut.getMonth(), checkOut.getDate(), 0, 0, 0, 0);

    const safeEnd = end <= start ? this.addDays(start, 1) : end;

    return [{
      title: `${titlePrefix}${H.name || booked?.hotelName || ''}`.trim(),
      location: loc,
      description: html,
      start,
      end: safeEnd,
      allDay: true,
      url: H.web || 'https://app.vyootrip.com/'
    }];
  }

  private buildTransportEvents(booked: any, t: 'flight'|'train'): CalendarEvent[] {
    const labelFlightTo = this.tt('my-bookeds.flight.flight-to', 'Vuelo a');
    const labelTrainTo  = this.tt('my-bookeds.train.train-to',  'Tren a');
    const labelTo = (t === 'flight') ? labelFlightTo : labelTrainTo;

    const outStart = this.pickBestDate(booked?.departLocalDate, booked?.departDate);
    const outEnd   = new Date(outStart);

    const out: CalendarEvent = {
      title: `${labelTo} ${booked?.to || ''}`.trim(),
      location: this.formatRoute(booked?.from, booked?.to),
      description: (t === 'flight') ? this.buildFlightHtml(booked, 'outbound') : this.buildTrainHtml(booked, 'outbound'),
      start: outStart,
      end: outEnd,
      allDay: false
    };

    const evs: CalendarEvent[] = [out];

    const hasReturn = !!(booked?.returnLocalDate || booked?.returnDate);
    if (hasReturn) {
      const retStart = this.pickBestDate(booked?.returnLocalDate, booked?.returnDate);
      const retEnd   = new Date(retStart);

      evs.push({
        title: `${labelTo} ${booked?.from || ''}`.trim(),
        location: this.formatRoute(booked?.to, booked?.from),
        description: (t === 'flight') ? this.buildFlightHtml(booked, 'return') : this.buildTrainHtml(booked, 'return'),
        start: retStart,
        end: retEnd,
        allDay: false
      });
    }
    return evs;
  }

  private buildCarEvents(booked: any): CalendarEvent[] {
    const labelCarTo = this.tt('event-calendar.car-to', 'Coche a');

    const outStart = this.toDate(booked?.departDate || booked?.checkIn || booked?.beginDate);
    const outEnd   = new Date(outStart);

    const evs: CalendarEvent[] = [{
      title: `${labelCarTo} ${booked?.to || ''}`.trim(),
      location: this.formatRoute(booked?.from, booked?.to),
      description: this.buildCarHtml(booked, 'outbound'),
      start: outStart,
      end: outEnd,
      allDay: false
    }];

    const hasReturn = !!(booked?.returnDate || booked?.checkOut || booked?.endDate);
    if (hasReturn) {
      const retStart = this.toDate(booked?.returnDate || booked?.checkOut || booked?.endDate);
      const retEnd   = new Date(retStart);
      evs.push({
        title: `${labelCarTo} ${booked?.from || ''}`.trim(),
        location: this.formatRoute(booked?.to, booked?.from),
        description: this.buildCarHtml(booked, 'return'),
        start: retStart,
        end: retEnd,
        allDay: false
      });
    }
    return evs;
  }

  private buildExpenseEvent(booked: any): CalendarEvent[] {
    const serviceLbl = this.tt('company.transaction.filters.service', 'Servicio');
    const start = this.toDate(booked?.beginDate || booked?.createdAt);
    const rawEnd = booked?.endDate ?? null;
    let end = rawEnd ? this.toDate(rawEnd) : new Date(start);
    return [{
      title: `${serviceLbl}: ${booked?.type || ''}`.trim(),
      location: '',
      description: '',
      start, end,
      allDay: false
    }];
  }

  private buildGenericEvent(booked: any): CalendarEvent[] {
    const start = this.toDate(booked?.checkIn || booked?.departDate || booked?.beginDate || booked?.createdAt);
    const end   = this.toDate(booked?.checkOut || booked?.returnDate || booked?.endDate || start);
    const baseDesc = (booked.comments || '').toString().trim() || 'https://app.vyootrip.com/';
    return [{
      title: booked?.travelTitle || 'Reserva',
      location: '',
      description: baseDesc,
      start, end,
      allDay: false
    }];
  }

  private buildGoogleCalendarUrl(ev: CalendarEvent) {
    const fmt = (d: Date, allDay: boolean) => {
      if (allDay) {
        const y = d.getFullYear(), m = String(d.getMonth()+1).padStart(2,'0'), day = String(d.getDate()).padStart(2,'0');
        return `${y}${m}${day}`;
      }
      const y = d.getUTCFullYear(), m = String(d.getUTCMonth()+1).padStart(2,'0'), day = String(d.getUTCDate()).padStart(2,'0');
      const hh = String(d.getUTCHours()).padStart(2,'0'), mm = String(d.getUTCMinutes()).padStart(2,'0'), ss = String(d.getUTCSeconds()).padStart(2,'0');
      return `${y}${m}${day}T${hh}${mm}${ss}Z`;
    };
    const start = fmt(ev.start, ev.allDay);
    const endDate = ev.allDay ? this.addDays(ev.end, 1) : ev.end;
    const end = fmt(endDate, ev.allDay);

    const params = new URLSearchParams({
      action: 'TEMPLATE',
      text: ev.title || '',
      details: ev.description || '',
      location: ev.location || '',
      dates: `${start}/${end}`,
    });
    return `https://calendar.google.com/calendar/render?${params.toString()}`;
  }

  private buildOutlookWebUrl(ev: CalendarEvent, host: 'live'|'office' = 'live') {
    const base = host === 'office' ? 'https://outlook.office.com' : 'https://outlook.live.com';

    const pad = (n: number) => String(n).padStart(2, '0');
    const ymd = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}`;

    const startParam = ev.allDay ? ymd(ev.start) : ev.start.toISOString();
    const endParam   = ev.allDay ? ymd(this.addDays(ev.end, 1)) : ev.end.toISOString();

    const params = new URLSearchParams({
      path: '/calendar/action/compose',
      rru: 'addevent',
      subject: ev.title || '',
      body: ev.description || '',
      location: ev.location || '',
      startdt: startParam,
      enddt: endParam
    });

    if (ev.allDay) params.set('allday', 'true');
    return `${base}/calendar/0/deeplink/compose?${params.toString()}`;
  }

  private buildGoogleCalendarUrlForHotel(booking: any) {
    const H = this.normalizeHotelBooking(booking);
    const title = `${this.tt('event-calendar.title', 'Reserva: ')}${H.name || ''}`;

    const dataTitle = this.tt('event-calendar.contact', 'Datos de contacto');
    const mailLbl   = this.tt('event-calendar.mail', 'Email');
    const phoneLbl  = this.tt('event-calendar.phone', 'Teléfono');
    const descLbl   = this.tt('event-calendar.accommodation', 'Descripción del alojamiento');

    const contactHtml = (H.email || H.phone || H.description)
      ? `<h2>${dataTitle}</h2><ul>` +
        (H.email ? `<li><b>${mailLbl}: </b>${this.escapeHtmlAllowBasic(H.email)}</li>` : '') +
        (H.phone ? `<li><b>${phoneLbl}: </b>${this.escapeHtmlAllowBasic(H.phone)}</li>` : '') +
        `</ul>` +
        (H.description ? `<br><h2>${descLbl}:</h2><br>${this.escapeHtmlAllowBasic(H.description)}` : '')
      : '';

    const extraTitle = this.tt('company.company-booked.booked-details', 'Detalles de la reserva');
    const locatorLbl = this.tt('my-bookeds.train-flight.locator', 'Localizador');
    const roomsLbl   = this.tt('hotel.custom.rooms', 'Habitaciones');
    const roomTypeLbl= this.tt('search.search-booked.room-type', 'Tipo de habitación');
    const boardLbl   = this.tt('my-bookeds.view.rules', 'Régimen');

    const extras: string[] = [];
    if (H.locator)  extras.push(`<li><b>${locatorLbl}: </b>${this.escapeHtmlAllowBasic(H.locator)}</li>`);
    if (H.rooms)    extras.push(`<li><b>${roomsLbl}: </b>${H.rooms}</li>`);
    if (H.roomType) extras.push(`<li><b>${roomTypeLbl}: </b>${this.escapeHtmlAllowBasic(H.roomType)}</li>`);
    if (H.boardType)extras.push(`<li><b>${boardLbl}: </b>${this.escapeHtmlAllowBasic(H.boardType)}</li>`);

    const customHtml = extras.length ? `<h2>${extraTitle}</h2><ul>${extras.join('')}</ul>` : '';
    const detailsHtml = (contactHtml || customHtml);

    const localization = H.zoneName ? `${H.address || ''},${H.zoneName}` : (H.address || '');

    const ymd = (d: Date) => {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${y}${m}${day}`;
    };

    const ci = this.toDate(booking?.checkIn);
    const co = booking?.checkOut ? this.toDate(booking.checkOut) : this.addDays(ci, 1);
    const end = co <= ci ? this.addDays(ci, 1) : co;
    const startDay = ymd(ci);
    const endDay   = ymd(this.addDays(end, 1));
    const dates    = `${startDay}/${endDay}`;

    const spropUrl  = H.web || 'https://app.vyootrip.com/';
    const spropName = 'Reserva de habitacion';

    const params = new URLSearchParams();
    params.set('action', 'TEMPLATE');
    params.set('text', title);
    params.set('dates', dates);
    if (localization) params.set('location', localization);
    if (detailsHtml)  params.set('details', detailsHtml);
    params.set('trp', 'false');
    params.append('sprop', spropUrl);
    params.append('sprop', `name:${spropName}`);

    return `https://www.google.com/calendar/event?${params.toString()}`;
  }

  private buildOutlookWebUrlForHotelIfNeeded(
    booking: any, ev: CalendarEvent, isHotel: boolean, host: 'live'|'office' = 'live'
  ) {
    if (!isHotel) return this.buildOutlookWebUrl(ev, host);

    const H = this.normalizeHotelBooking(booking);
    const title = `${this.tt('event-calendar.title', 'Reserva: ')}${(H.name || booking?.hotelName || booking?.destinationName || '')}`;

    const dataTitle = this.tt('event-calendar.contact', 'Datos de contacto');
    const mailLbl   = this.tt('event-calendar.mail', 'Email');
    const phoneLbl  = this.tt('event-calendar.phone', 'Teléfono');
    const descLbl   = this.tt('event-calendar.accommodation', 'Descripción del alojamiento');

    const contactHtml = (H.email || H.phone || H.description)
      ? `<h2>${dataTitle}</h2><ul>` +
        (H.email ? `<li><b>${mailLbl}: </b>${this.escapeHtmlAllowBasic(H.email)}</li>` : '') +
        (H.phone ? `<li><b>${phoneLbl}: </b>${this.escapeHtmlAllowBasic(H.phone)}</li>` : '') +
        `</ul>` +
        (H.description ? `<br><h2>${descLbl}:</h2><br>${this.escapeHtmlAllowBasic(H.description)}` : '')
      : '';

    const extraTitle  = this.tt('company.company-booked.booked-details', 'Detalles de la reserva');
    const locatorLbl  = this.tt('my-bookeds.train-flight.locator', 'Localizador');
    const roomsLbl    = this.tt('hotel.custom.rooms', 'Habitaciones');
    const roomTypeLbl = this.tt('search.search-booked.room-type', 'Tipo de habitación');
    const boardLbl    = this.tt('my-bookeds.view.rules', 'Régimen');

    const extras: string[] = [];
    if (H.locator)   extras.push(`<li><b>${locatorLbl}: </b>${this.escapeHtmlAllowBasic(H.locator)}</li>`);
    if (H.rooms)     extras.push(`<li><b>${roomsLbl}: </b>${H.rooms}</li>`);
    if (H.roomType)  extras.push(`<li><b>${roomTypeLbl}: </b>${this.escapeHtmlAllowBasic(H.roomType)}</li>`);
    if (H.boardType) extras.push(`<li><b>${boardLbl}: </b>${this.escapeHtmlAllowBasic(H.boardType)}</li>`);

    const customHtml = extras.length ? `<h2>${extraTitle}</h2><ul>${extras.join('')}</ul>` : '';
    const bodyHtml = (contactHtml || customHtml || ev.description || '');
    const location = H.zoneName ? `${H.address || ''}, ${H.zoneName}` : (H.address || ev.location || '');

    const base = host === 'office' ? 'https://outlook.office.com' : 'https://outlook.live.com';

    const pad = (n: number) => String(n).padStart(2, '0');
    const ymd = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}`;
    const startParam = ev.allDay ? ymd(ev.start) : ev.start.toISOString();
    const endParam   = ev.allDay ? ymd(this.addDays(ev.end, 1)) : ev.end.toISOString();

    const params = new URLSearchParams({
      path: '/calendar/action/compose',
      subject: title,
      body: bodyHtml,
      location,
      startdt: startParam,
      enddt: endParam
    });
    if (ev.allDay) params.set('allday', 'true');

    return `${base}/calendar/0/deeplink/compose?${params.toString()}`;
  }

  private buildIcsUrlAndNameMulti(evs: CalendarEvent[]) {
    const escapeIcs = (text: string) => (text || '')
      .replace(/\\/g, '\\\\').replace(/;/g, '\\;').replace(/,/g, '\\,').replace(/\n/g, '\\n');

    const plain = (html: string) => this.stripHtml(html || '');

    const fmt = (d: Date, allDay: boolean) => {
      if (allDay) {
        const y = d.getFullYear(), m = String(d.getMonth()+1).padStart(2,'0'), day = String(d.getDate()).padStart(2,'0');
        return `${y}${m}${day}`;
      }
      const y = d.getUTCFullYear(), m = String(d.getUTCMonth()+1).padStart(2,'0'), day = String(d.getUTCDate()).padStart(2,'0');
      const hh = String(d.getUTCHours()).padStart(2,'0'), mm = String(d.getUTCMinutes()).padStart(2,'0'), ss = String(d.getUTCSeconds()).padStart(2,'0');
      return `${y}${m}${day}T${hh}${mm}${ss}Z`;
    };

    const alarmBlocksFor = (allDay: boolean) =>
      allDay
        ? ['BEGIN:VALARM','ACTION:DISPLAY','DESCRIPTION:Reminder','TRIGGER:-P1D','END:VALARM']
        : [
            'BEGIN:VALARM','ACTION:DISPLAY','DESCRIPTION:Reminder','TRIGGER:-P1D','END:VALARM',
            'BEGIN:VALARM','ACTION:DISPLAY','DESCRIPTION:Reminder','TRIGGER:-PT3H','END:VALARM'
          ];

    const lines: string[] = ['BEGIN:VCALENDAR','VERSION:2.0','PRODID:-//Vyootrip//Calendar//ES','CALSCALE:GREGORIAN','METHOD:PUBLISH'];

    evs.forEach(ev => {
      const uid = (window.crypto && 'randomUUID' in window.crypto) ? crypto.randomUUID()
        : (Date.now() + Math.random() + '@vyootrip');

      const dtStart = fmt(ev.start, ev.allDay);
      const endForAllDay = ev.allDay ? this.addDays(ev.end, 1) : ev.end;
      const dtEnd   = fmt(endForAllDay, ev.allDay);

      lines.push(
        'BEGIN:VEVENT',
        `UID:${uid}`,
        `SUMMARY:${escapeIcs(ev.title)}`,
        `DTSTART:${dtStart}`,
        `DTEND:${dtEnd}`,
        `DESCRIPTION:${escapeIcs(plain(ev.description))}`,
        ev.location ? `LOCATION:${escapeIcs(ev.location)}` : '',
        `URL:${escapeIcs(ev.url || 'https://app.vyootrip.com/')}`
      );
      lines.push(...alarmBlocksFor(ev.allDay));
      lines.push('END:VEVENT');
    });

    lines.push('END:VCALENDAR');

    const blob = new Blob([lines.join('\r\n')], { type: 'text/calendar;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const name = evs.length === 1
      ? this.slugify(evs[0].title) + '.ics'
      : (this.slugify(evs[0].title.split(':')[0] || 'eventos') + '-roundtrip.ics');

    return { icsUrl: url, icsFileName: name };
  }

  onAddToCalendar() {
    const events = this.buildCalendarEvents(this.booked, this.type);
    if (!events.length) {
      const msg = this.tt('event-calendar.create-error', 'No se ha podido crear el evento');
      this.ngxToastrService.typeError(msg, '');
      return;
    }

    const isHotel = ['bookingapireference', 'custom-booking', 'hotel']
                      .includes((this.type || '').toLowerCase());

    const googleUrls  = isHotel
      ? [ this.buildGoogleCalendarUrlForHotel(this.booked) ]
      : events.map(ev => this.buildGoogleCalendarUrl(ev));

    const outlookPersonalUrls = isHotel
      ? [ this.buildOutlookWebUrlForHotelIfNeeded(this.booked, events[0], true, 'live') ]
      : events.map(ev => this.buildOutlookWebUrl(ev, 'live'));

    const outlookWorkUrls = isHotel
      ? [ this.buildOutlookWebUrlForHotelIfNeeded(this.booked, events[0], true, 'office') ]
      : events.map(ev => this.buildOutlookWebUrl(ev, 'office'));

    const { icsUrl, icsFileName } = this.buildIcsUrlAndNameMulti(events);

    const modalRef = this.modalService.open(AddToCalendarComponent, { centered: true, size: 'md' });
    modalRef.componentInstance.googleUrl          = googleUrls;
    modalRef.componentInstance.outlookPersonalUrl = outlookPersonalUrls;
    modalRef.componentInstance.outlookWorkUrl     = outlookWorkUrls;
    modalRef.componentInstance.icsUrl             = icsUrl;
    modalRef.componentInstance.icsFileName        = icsFileName;
  }

  onChangeStatus() {
   /*  this.translate.get([
      "common.are-u-sure",
      "flight.detail-flight.avail-out-text",
      "project.accept"
    ]).pipe(take(1)).subscribe((t) => {
      const text =
        t["flight.detail-flight.avail-out-text"] ||
        "No hay disponibilidad para este vuelo con las opciones seleccionadas. Por favor, elige otra opción.";
      const accept =
        t["project.accept"] || "Aceptar";

      swal.fire({
        icon: "warning",
        title: t["common.are-u-sure"],
        text: ,
        showCancelButton: true,
        confirmButtonText: accept,
        cancelButtonText: accept,
        customClass: {
          popup: "price-change-popup",
          confirmButton: "simple-confirm-btn",
        },
        buttonsStyling: false,
      }).then((result) => {
        if (result.isConfirmed) {
          
        }
      });
    }); */
    this.bookService.onChangeStatus(this.booked, this.type);
  }

  private handleEditValidationError(
    err: any,
    modalRef: any,
    validationType: string,
    fallbackMessageKey?: string
  ): boolean {
    modalRef.componentInstance.loading = false;

    const errorBody = err?.error || {};
    const validationErrors = Array.isArray(errorBody.validationErrors)
      ? errorBody.validationErrors
      : [];

    if (err.status === 422 && validationErrors.length > 0) {
      const validationMessage = this.utilsService.getValidationErrorsMessage(
        validationErrors,
        validationType
      );

      if (typeof modalRef.componentInstance.applyBackendValidationErrors === 'function') {
        modalRef.componentInstance.applyBackendValidationErrors(validationErrors);
      }

      this.ngxToastrService.typeInfo(
        'ERROR',
        validationMessage || errorBody.message || this.translate.instant('common.validation.form-invalid'),
        10000,
        { enableHtml: true }
      );

      return true;
    }

    const fallbackMessage = fallbackMessageKey
      ? this.translate.instant(fallbackMessageKey)
      : this.translate.instant('common.some-error');

    this.ngxToastrService.typeError(
      fallbackMessage,
      errorBody.message || errorBody.text || fallbackMessage
    );

    return false;
  }

}
